import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { AzureHorizonDBTableDataset } from 'app/model/dataset/azure-horizon-db-table.dataset';
import {
    tabularPropertiesCommonAdapts,
    TabularTypeDatasetAdapter,
} from './tabular-type.dataset.adapter';
import { GlobalVariables } from 'app/services/globals';

// Azure HorizonDB is PostgreSQL wire-compatible, so it reuses the exact same serialization spec
// as Azure Database for PostgreSQL; only the emitted dataset type differs ('AzureHorizonDBTable').
// Unlike PostgreSQL, HorizonDB is newly introduced and has no driver-version concept. In Fabric the
// driver version lives on the copy activity source/sink settings (the dataset `version`), so it is
// intentionally NOT serialized for HorizonDB; the connector always behaves as the modern driver.
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureHorizonDBTableDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            ...tabularPropertiesCommonAdapts,
            typeProperties: {
                tableName: '*',
                schema: '*',
                table: '*'
            },
            connectionProperties: GlobalVariables.connectionPropertiesForPostgreSql ? '*' : undefined
        }
    }
})
export class AzureHorizonDBDatasetAdapter extends TabularTypeDatasetAdapter<
    AzureHorizonDBTableDataset,
    Model.DatasetResource
> {
    protected async _toResource(dataset: AzureHorizonDBTableDataset): Promise<Model.DatasetResource> {
        const datasetResource = await this._toResourceInternal(
            dataset,
            Metamodel.modelFor(AzureHorizonDBTableDataset).name
        );
        // HorizonDB has no version concept on any platform: never surface `version` in the payload.
        delete datasetResource.properties.version;
        return datasetResource;
    }

    protected async _toModel(
        modelDatasetResource: Model.DatasetResource,
        dataset?: AzureHorizonDBTableDataset
    ): Promise<AzureHorizonDBTableDataset> {
        const modelDataset = await this._toModelInternal(
            modelDatasetResource,
            dataset,
            AzureHorizonDBTableDataset.MODEL
        );
        // HorizonDB has no version concept on any platform; ensure the model never carries one, even
        // if an older payload happened to include it. The modern connection path is preserved by the
        // dataset's shouldHonorConnectionVersion() override, not by the version value.
        modelDataset.version = undefined;
        return modelDataset;
    }
}
